import { useState, useEffect, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getOts, getOtConfig, getProjects } from '../api/client'
import { useAuth } from '../context/AuthContext'
import PageHeader from '../components/PageHeader'
import OtPanel from '../components/ot/OtPanel'
import TarjetaOt from '../components/ot/TarjetaOt'
import { agrupar, filtrar, tonoDe, verDevueltas, tituloOt } from '../components/ot/otLogica'
import ListaOt from '../components/ot/ListaOt'
import { ControlZoom, ZonaZoom } from '../components/ot/zoom'
import { useZoom } from '../components/ot/useZoom'
import { Semaforo } from '../components/ot/Etiquetas'
import { hoyLocal } from '../components/partes/fechas'

// Operaciones > Planta > Órdenes de trabajo: every OT in its stage of the
// flow. The open panel lives in the address (?ot=<id>), so a request can be
// linked straight to it.
export default function PlantaOT() {
  const { t } = useTranslation(['operations', 'common'])
  const navigate = useNavigate()
  const { user } = useAuth()
  const [ots, setOts] = useState(null)
  const [config, setConfig] = useState(null)
  const [proyectos, setProyectos] = useState([])
  const [error, setError] = useState('')
  const [sp, setSp] = useSearchParams()
  // ?buscar= opens filtered (a new batch: its L-<n>).
  const [filtro, setFiltro] = useState(() => ({ buscar: sp.get('buscar') || '', origen: '', proyectoId: '', soloMias: false, tipo: '' }))
  const [verFinales, setVerFinales] = useState(false)
  const [marcoDevueltas, setMarcoDevueltas] = useState(false)
  const [densidad, setDensidadEstado] = useState(() => { try { return localStorage.getItem('ot_densidad') === 'compacta' ? 'compacta' : 'normal' } catch { return 'normal' } })
  const setDensidad = (d) => { setDensidadEstado(d); try { localStorage.setItem('ot_densidad', d) } catch { /* private mode */ } }
  const zoomTablero = useZoom('ot_zoom_tablero')
  const zoomLista = useZoom('ot_zoom_lista')
  const panelId = sp.get('ot') ? Number(sp.get('ot')) : null
  const vista = sp.get('vista') === 'lista' ? 'lista' : 'tablero'
  const setVista = (v) => setSp(prev => {
    const n = new URLSearchParams(prev)
    if (v === 'lista') n.set('vista', 'lista')
    else n.delete('vista')
    return n
  })
  const abrir = (id) => setSp(prev => {
    const n = new URLSearchParams(prev)
    if (id == null) n.delete('ot')
    else n.set('ot', String(id))
    return n
  })

  const cargar = useCallback(() => {
    getOts().then(o => { setOts(o); setError('') }).catch(() => setError(t('ot.cargaFallo')))
  }, [t])
  useEffect(() => { cargar() }, [cargar])
  useEffect(() => {
    getOtConfig().then(setConfig).catch(() => {})
    getProjects().then(setProyectos).catch(() => {})
  }, [])

  const hoy = hoyLocal()
  const vistas = useMemo(() => filtrar(ots || [], { ...filtro, yoId: user?.id }), [ots, filtro, user])
  const conDevueltas = verDevueltas(ots || [], user?.id, marcoDevueltas)
  const { carriles, borradores } = useMemo(() => agrupar(vistas, { verFinales, ocultar: conDevueltas ? [] : ['devueltas'] }),
    [vistas, verFinales, conDevueltas])
  const puedeSolicitar = config && (config.funciones.includes('solicitar') || config.funciones.includes('proponer_reposicion'))

  return (
    <div className="p-8">
      <div className="mb-6">
        <PageHeader title={t('ot.titulo')} subtitle={t('ot.subtitulo')}
          actions={puedeSolicitar && (
            <button onClick={() => navigate('/operations/planta/ot/nueva')}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors">
              {t('ot.nueva.boton')}
            </button>
          )} />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
          {['tablero', 'lista'].map(v => (
            <button key={v} type="button" onClick={() => setVista(v)}
              className={`px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors ${vista === v ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>
              {t(`ot.vistas.${v}`)}
            </button>
          ))}
        </div>
        {vista === 'tablero' && (
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5" role="group" aria-label={t('ot.densidad.titulo')} title={t('ot.densidad.ayuda')}>
            {['normal', 'compacta'].map(d => (
              <button key={d} type="button" onClick={() => setDensidad(d)} aria-pressed={densidad === d}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${densidad === d ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>
                {t(`ot.densidad.${d}`)}
              </button>
            ))}
          </div>
        )}
        <ControlZoom z={vista === 'lista' ? zoomLista : zoomTablero} />
        <span className="text-[11px] text-gray-400 hidden lg:inline">{t('ot.zoom.ayuda')}</span>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input value={filtro.buscar} onChange={e => setFiltro(f => ({ ...f, buscar: e.target.value }))}
          placeholder={t('ot.buscar')} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-72 bg-white" />
        <select value={filtro.tipo} onChange={e => setFiltro(f => ({ ...f, tipo: e.target.value }))}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white">
          <option value="">{t('ot.tipoFiltro.todos')}</option>
          <option value="fabricacion">{t('ot.tipoFiltro.fabricacion')}</option>
          <option value="mantenimiento">{t('ot.tipoFiltro.mantenimiento')}</option>
          <option value="servicio">{t('ot.tipoFiltro.servicio')}</option>
        </select>
        <select value={filtro.origen} onChange={e => setFiltro(f => ({ ...f, origen: e.target.value }))}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white">
          <option value="">{t('ot.todosOrigenes')}</option>
          {['proyecto', 'reposicion'].map(o => <option key={o} value={o}>{t(`ot.origenes.${o}`)}</option>)}
        </select>
        <select value={filtro.proyectoId} onChange={e => setFiltro(f => ({ ...f, proyectoId: e.target.value }))}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white max-w-[220px]">
          <option value="">{t('ot.todosProyectos')}</option>
          {proyectos.map(p => <option key={p.id} value={p.id}>{p.name}{p.code ? ` (${p.code})` : ''}</option>)}
        </select>
        <label className="inline-flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" checked={filtro.soloMias} onChange={e => setFiltro(f => ({ ...f, soloMias: e.target.checked }))} />
          {t('ot.soloMias')}
        </label>
        {vista === 'tablero' && (
          <>
            <label className="inline-flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" checked={conDevueltas} disabled={conDevueltas && !marcoDevueltas}
                title={conDevueltas && !marcoDevueltas ? t('ot.devueltasTuyas') : undefined}
                onChange={e => setMarcoDevueltas(e.target.checked)} />
              {t('ot.verDevueltas')}
            </label>
            <label className="inline-flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" checked={verFinales} onChange={e => setVerFinales(e.target.checked)} />
              {t('ot.verCerradas')}
            </label>
          </>
        )}
        <span className="inline-flex items-center gap-3 text-[11px] text-gray-500">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />{t('ot.leyenda.verde')}</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" />{t('ot.leyenda.amarillo')}</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500" />{t('ot.leyenda.rojo')}</span>
        </span>
        {ots && <span className="text-xs text-gray-400 ml-auto">{t('ot.nMostradas', { n: vistas.filter(o => o.estado !== 'borrador').length })}</span>}
      </div>

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

      {vista === 'tablero' && borradores.length > 0 && (
        <div className="mb-4 rounded-xl border border-dashed border-gray-300 bg-white px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{t('ot.misBorradores', { n: borradores.length })}</p>
          <div className="flex flex-wrap gap-2">
            {borradores.map(o => (
              <button key={o.id} type="button" onClick={() => abrir(o.id)}
                className={`inline-flex items-center gap-1.5 text-left rounded-lg border border-gray-200 border-l-4 px-3 py-1.5 text-sm transition-colors ${tonoDe(o.semaforo)}`}>
                <Semaforo semaforo={o.semaforo} />
                <span className="font-mono text-xs text-gray-500 mr-1.5">{o.codigo}</span>
                <span className="text-gray-800">{tituloOt(o) || t('ot.sinArticulo')}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {!ots ? (
        !error && <p className="text-sm text-gray-400">{t('common:loading', { defaultValue: 'Cargando…' })}</p>
      ) : vista === 'lista' ? (
        <ZonaZoom z={zoomLista}>
          <ListaOt ots={vistas} onAbrir={abrir} onActualizar={cargar}
            puedeSoltarOf={!!config?.funciones?.includes('ejecutar_cola_sap')} />
        </ZonaZoom>
      ) : (
        <ZonaZoom z={zoomTablero} ajustarAlto className="pb-1">
          <div className="flex gap-3 items-stretch">
            {carriles.map(c => (
              <section key={c.id} className={`${c.fondo} rounded-xl border border-gray-200 border-t-4 ${c.acento} p-2 ${densidad === 'compacta' ? 'basis-80 max-w-[40rem]' : 'basis-64 max-w-[28rem]'} grow shrink-0 min-h-[16rem] flex flex-col`}>
                <div className="relative flex items-center justify-between px-1.5 py-1 mb-2">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">{t(`ot.carriles.${c.id}`)}</span>
                    <Ayuda texto={t(`ot.carrilesAyuda.${c.id}`)} />
                  </span>
                  <span className="text-xs text-gray-400">{c.ots.length}</span>
                </div>
                <div className={`flex-1 ${densidad === 'compacta' ? 'space-y-1.5' : 'space-y-2'}`}>
                  {!c.ots.length && <p className="text-center text-xs text-gray-400 py-6">—</p>}
                  {c.ots.map(o => <TarjetaOt key={o.id} o={o} hoy={hoy} compacta={densidad === 'compacta'} onClick={() => abrir(o.id)} />)}
                </div>
              </section>
            ))}
          </div>
        </ZonaZoom>
      )}

      {panelId != null && (
        <OtPanel key={panelId} otId={panelId} onClose={() => abrir(null)} onChanged={cargar} />
      )}
    </div>
  )
}

// A lane's "?": what stage of the flow its OTs are in, on hover or focus.
function Ayuda({ texto }) {
  return (
    <span className="group static">
      <button type="button" aria-label={texto}
        className="w-4 h-4 rounded-full border border-gray-300 text-[10px] font-semibold leading-none text-gray-400 hover:text-gray-700 hover:border-gray-500 focus:outline-none focus:text-gray-700 focus:border-gray-500">
        ?
      </button>
      <span role="tooltip"
        className="pointer-events-none absolute left-0 right-0 top-full z-20 mt-1 hidden rounded-lg bg-gray-900 px-3 py-2 text-xs font-normal normal-case tracking-normal text-white shadow-lg group-hover:block group-focus-within:block">
        {texto}
      </span>
    </span>
  )
}
